'use es6';

import ImmutablePropTypes from 'react-immutable-proptypes';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import UITab from 'UIComponents/nav/UITab';
import UITabs from 'UIComponents/nav/UITabs';
import FormattedMessage from 'I18n/components/FormattedMessage';
import LessonOverview from './LessonOverview';
import ResourcesList from './ResourcesList';
import SmartNavigator from '../../containers/Navigator/SmartNavigator';
import UsageTracker from '../../utils/UsageTracker';
import { device } from '../../constants/StyledComponentConstants';
import { useLessonContentById } from '../../features/lessons';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const StyledUITabs = styled(UITabs).withConfig({
  displayName: "LessonInfoTabs__StyledUITabs",
  componentId: "sc-1jsm2sn-0"
})(["@media screen and ", "{width:100vw;}"], device.tablet);
const StyledUITab = styled(UITab).withConfig({
  displayName: "LessonInfoTabs__StyledUITab",
  componentId: "sc-1jsm2sn-1"
})(["@media screen and ", "{padding-left:10px !important;padding-right:10px !important;}"], device.tablet);
const MobileOnlyTab = styled(StyledUITab).withConfig({
  displayName: "LessonInfoTabs__MobileOnlyTab",
  componentId: "sc-1jsm2sn-2"
})(["@media screen and ", "{display:none;}padding:0;"], device.not.tablet);
const NegativeMargin = styled.div.withConfig({
  displayName: "LessonInfoTabs__NegativeMargin",
  componentId: "sc-1jsm2sn-3"
})(["margin:-16px;"]);

/**
 * Including tabId & title PropTypes to silence UIComponents validation noise
 * in console:
 *
 * "Warning: Failed prop type: Controllable(UITabs): All children must be
 * instances of UITab."
 *
 * See https://hubspot.slack.com/archives/C1WG3L3J6/p1573133826232300
 * See https://git.hubteam.com/HubSpot/UIComponents/blob/892bb7ef/UIComponents/static/js//nav/UITabs.js#L37-L40
 */
StyledUITab.propTypes = MobileOnlyTab.propTypes = {
  tabId: PropTypes.string,
  title: PropTypes.object
};
const onTabChange = ({
  currentTarget
}) => {
  UsageTracker.track('interaction', {
    action: 'lesson-info-tab-clicked',
    tab: currentTarget.value
  });
};
const LessonInfoTabs = ({
  lesson,
  onResourceClick,
  contentId
}) => {
  const content = useLessonContentById({
    lessonId: lesson.get('id'),
    contentId: Number(contentId),
    language: lesson.get('language')
  });
  const handleOrganisationUrlClick = (professorId, organisation, organisationUrl) => () => {
    UsageTracker.track('interaction', {
      action: 'professor-organisation-click',
      contentType: 'LESSON',
      appearsIn: 'lessons',
      organisation,
      organisationUrl,
      professorId,
      lessonId: lesson.get('id'),
      contentId
    });
  };
  return /*#__PURE__*/_jsxs(StyledUITabs, {
    defaultSelected: "lesson-info",
    fill: true,
    onSelectedChange: onTabChange,
    children: [/*#__PURE__*/_jsx(StyledUITab, {
      tabId: "lesson-info",
      title: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "learningCenter.aboutThisLesson"
      }),
      children: /*#__PURE__*/_jsx(LessonOverview, {
        description: lesson.get('description'),
        handleOrganisationUrlClick: handleOrganisationUrlClick,
        hubId: content === null || content === void 0 ? void 0 : content.hubId,
        professors: lesson.get('professors')
      })
    }), /*#__PURE__*/_jsx(StyledUITab, {
      tabId: "resources",
      title: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "learningCenter.resources.title"
      }),
      children: /*#__PURE__*/_jsx(ResourcesList, {
        additionalResources: lesson.get('learningMaterials') ? lesson.get('learningMaterials').toJS() : undefined,
        i18nHeading: "learningCenter.lessonResources",
        i18nNoResources: "learningCenter.noLessonResources",
        onResourceClick: onResourceClick,
        slidesUrl: lesson.get('slidesUrl'),
        transcriptUrl: lesson.get('transcriptUrl')
      })
    }), /*#__PURE__*/_jsx(MobileOnlyTab, {
      tabId: "lessons",
      title: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "learningCenter.lessonNavigator.title"
      }),
      children: /*#__PURE__*/_jsx(NegativeMargin, {
        children: /*#__PURE__*/_jsx(SmartNavigator, {})
      })
    })]
  });
};
LessonInfoTabs.propTypes = {
  contentId: PropTypes.number.isRequired,
  lesson: ImmutablePropTypes.map.isRequired,
  onResourceClick: PropTypes.func
};
export default LessonInfoTabs;